import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material"
import { Fragment, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { FormProvider, useForm } from "react-hook-form"
import DeleteDialog from "@components/DeleteDialog"
import DiscardDialog from "@components/DiscardDialog"
import {
  productsApi,
  useDeleteProductMutation,
  useLazyGetProductByKeyQuery,
  useUpdateProductMutation,
} from "@store/services/productsService"
import { useOrgDivOptions } from "@hooks/useOrgDivOptions"
import { getProductFormFields } from "./const"
import { FormBuilder } from "@components/FormBuilder"
import { UpdateProductDto } from "@custom-types/products"
import SuccessDialog from "@components/SuccessDialog"
import FamiliesTable from "./FamiliesTable"
import { GenericForm } from "@custom-types/form"
import { useAppDispatch } from "@store/hooks"
import EditNodeTagsDialog from "@features/administration/products/EditProduct/EditNodeTagsDialog"
import EditGraphTagsDialog from "@features/administration/products/EditProduct/EditGraphTagsDialog"
import { SplitButton } from "@features/results/Result/SplitButton"

export const EditProduct = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { key, orgKey, divKey } = useParams()
  const [fetchProduct, { data: productData, isLoading: isGetLoading }] =
    useLazyGetProductByKeyQuery()
  const [deleteProduct, { isSuccess: isDeleteSuccess, isLoading: isDeleteLoading }] =
    useDeleteProductMutation()
  const [updateProduct, { isSuccess: isUpdateSuccess, isLoading: isUpdateLoading }] =
    useUpdateProductMutation()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDiscardOpen, setIsDiscardOpen] = useState(false)
  const [isEditNodeTagsOpen, setIsEditNodeTagsOpen] = useState(false)
  const [isEditGraphTagsOpen, setIsEditGraphTagsOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const formMethods = useForm<UpdateProductDto>({
    defaultValues: {
      description: "",
      active: true,
      is_external: false,
    },
  })
  const { organisationOptions, divisionOptions } = useOrgDivOptions(
    productData?.organisation_key ?? "",
  )
  const [productFormFields, setProductFormFields] = useState<GenericForm>({})

  const onCancelClick = () => {
    setIsDiscardOpen(true)
  }

  const onSubmit = (data: UpdateProductDto) =>
    productData &&
    updateProduct({
      key: productData.key,
      organisation_key: productData.organisation_key,
      division_key: productData.division_key,
      body: data,
    })

  const onDeleteClickHandler = () => {
    setIsDeleteOpen(false)
    productData &&
      deleteProduct({
        key: productData.key,
        organisation_key: productData.organisation_key,
        division_key: productData.division_key,
      })
  }

  useEffect(() => {
    if (productData) {
      formMethods.reset(productData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [productData])

  useEffect(() => {
    setProductFormFields(getProductFormFields({ organisationOptions, divisionOptions }))
  }, [organisationOptions, divisionOptions])

  useEffect(() => {
    if (key && orgKey && divKey && !isDeleteSuccess) {
      fetchProduct({ key, orgKey, divKey })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [key, orgKey, divKey])

  useEffect(() => {
    if (isDeleteSuccess) {
      setSuccessMessage("Product deleted successfully.")
    }
  }, [isDeleteSuccess])

  useEffect(() => {
    if (isUpdateSuccess) {
      setSuccessMessage("Product saved successfully.")
    }
  }, [isUpdateSuccess])

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Edit Product Form
      </Typography>
      {isGetLoading || isUpdateLoading || isDeleteLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        productFormFields &&
        productData && (
          <FormProvider {...formMethods}>
            <form autoComplete={"off"} onSubmit={formMethods.handleSubmit(onSubmit)}>
              <Grid container={true} spacing={2} alignItems="center">
                {Object.keys(productFormFields).map((fieldName) => (
                  <Fragment key={fieldName}>
                    <FormBuilder
                      field={fieldName}
                      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                      component={productFormFields[fieldName]}
                    />
                  </Fragment>
                ))}
                <Grid item xs={12}>
                  <SplitButton
                    properties={{ onClick: () => setIsEditNodeTagsOpen(true) }}
                    title="Edit node level tags"
                    instruction={
                      <ul>
                        <li>
                          These tags will be available at the node level for every graph within this
                          product.
                        </li>
                        <li>On every node you will be able to set values of these tags.</li>
                      </ul>
                    }
                  />
                  <SplitButton
                    properties={{ onClick: () => setIsEditGraphTagsOpen(true) }}
                    title="Edit graph level tags"
                    instruction={
                      <ul>
                        <li>These tags will be added to all graphs of this product.</li>
                        <li>You will be able to set their value in graph details.</li>
                        <li>
                          If you set value of a tag on your graph, you will be able to attach it on
                          a node level.
                        </li>
                      </ul>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 4 }}>
                    {!isDeleteSuccess && (
                      <>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Graphs
                        </Typography>
                        <FamiliesTable
                          productKey={productData.key}
                          organisationKey={productData.organisation_key}
                          divisionKey={productData.division_key}
                        />
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid item={true} xs={12} sx={{ mt: 4 }}>
                  <Button variant="contained" type="submit">
                    Update
                  </Button>
                  <Button
                    sx={{ ml: 1 }}
                    variant="contained"
                    color={"error"}
                    type="button"
                    onClick={onCancelClick}
                  >
                    Cancel
                  </Button>
                  <Button
                    sx={{ ml: 1 }}
                    variant="contained"
                    color={"error"}
                    type="button"
                    onClick={() => setIsDeleteOpen(true)}
                  >
                    Delete
                  </Button>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        )
      )}
      <DeleteDialog
        open={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        onDeleteClick={onDeleteClickHandler}
      />
      <DiscardDialog
        open={isDiscardOpen}
        setOpen={setIsDiscardOpen}
        onAcceptClick={() => {
          navigate("/Administration/Products")
        }}
      />
      <SuccessDialog
        open={!!successMessage}
        onCloseClick={() => {
          dispatch(productsApi.util.resetApiState())
          navigate("/Administration/Products")
        }}
        successMessage={successMessage}
      />
      {productData && isEditNodeTagsOpen && (
        <EditNodeTagsDialog product={productData} onClose={() => setIsEditNodeTagsOpen(false)} />
      )}
      {productData && isEditGraphTagsOpen && (
        <EditGraphTagsDialog product={productData} onClose={() => setIsEditGraphTagsOpen(false)} />
      )}
    </Box>
  )
}
